import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Search, RefreshCw, ChevronLeft, ChevronRight, Activity  } from 'lucide-react';
import { useFontSize } from './FontSizeContext';
import toast from 'react-hot-toast';
import { usePermissions } from './usePermissions';
import { useTranslation } from 'react-i18next'; 

const SLAList = () => {
    const navigate = useNavigate();
    const { fontSize } = useFontSize();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const { canCreate, canUpdate, canDelete } = usePermissions('sla');
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    const fetchSLA = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/sla`, {
                params: { page, q: searchTerm },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setData(res.data.data);
            setTotal(res.data.total);
        } finally { setLoading(false); }
    }, [page, searchTerm]);

    useEffect(() => { fetchSLA(); }, [fetchSLA]);

    return (
        <div className={`flex flex-col h-full bg-[#f3f4f6] font-black uppercase tracking-tighter ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }}>
            <div className="bg-white p-4 border-b shadow-sm flex justify-between items-center px-8">
                <div className="flex items-center gap-6">
                    <h2 className="text-[#1a3353] text-xl italic flex items-center gap-2">
                        <Activity size={24} className="text-orange-600"/> {t('sla.list.title')}
                    </h2>
                    <div className="relative">
                        <Search className="absolute left-3 top-2.5 text-gray-400" size={16}/>
                        <input className="pl-10 pr-4 py-2 border w-[400px] outline-none focus:border-blue-600 rounded-sm shadow-inner" 
                               placeholder="Search SLA # or Description..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                </div>
                {canCreate && (
                <button onClick={() => navigate('/sla/new')} className="bg-[#1a3353] text-white px-8 py-2.5 rounded-sm flex items-center gap-2 hover:bg-black transition-all shadow-lg">
                    <Plus size={18}/> {t('sla.list.new')}
                </button>
                )}
            </div>

            <div className="flex-1 p-6 overflow-auto">
                <div className="bg-white border-2 border-gray-200 rounded shadow-2xl overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-[#1a3353] text-white sticky top-0 z-10">
                            <tr>
                                <th className="p-4 border-r border-white/10">{t('sla.header.slanum')}</th>
                                <th className="p-4 border-r border-white/10">{t('wo.fields.description')}</th>
                                <th className="p-4 border-r border-white/10">Type</th>
                                <th className="p-4 border-r border-white/10">Ranking</th>
                                <th className="p-4 text-center">{t('wo.headers.status')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr><td colSpan="5" className="p-20 text-center animate-pulse">Syncing SLA Master Data...</td></tr>
                            ) : data.map((s, i) => (
                                <tr key={i} onClick={() => navigate(`/sla/${s.slanum}`)} className="hover:bg-blue-50 cursor-pointer transition-all group">
                                    <td className="p-4 border-r font-black text-blue-900 group-hover:underline">{s.slanum}</td>
                                    <td className="p-4 border-r text-gray-600 italic">{s.description}</td>
                                    <td className="p-4 border-r text-center">{s.sla_type}</td>
                                    <td className="p-4 border-r text-center font-mono text-orange-600">{s.ranking}</td>
                                    <td className="p-4 text-center">
                                        <span className={`px-4 py-1 rounded-sm text-[9px] ${s.status === 'ACTIVE' ? 'bg-green-600 text-white' : 'bg-orange-500 text-white'}`}>{s.status}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="bg-[#1a3353] text-white p-3 px-10 flex justify-between items-center shadow-inner">
                <span className="text-[10px]">Total SLAs: <span className="text-orange-400 ml-2">{total}</span></span>
                <div className="flex gap-2">
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="p-2 border border-white/20 rounded-sm hover:bg-orange-500 disabled:opacity-20"><ChevronLeft size={18}/></button>
                    <button onClick={() => setPage(p => p + 1)} className="p-2 border border-white/20 rounded-sm hover:bg-orange-500"><ChevronRight size={18}/></button>
                </div>
            </div>
        </div>
    );
};

export default SLAList;